import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useOnScreen from 'shared/hooks/useOnScreen'

interface IUseLoading {
  src: string
  onLoad: () => void
}

const useLoading = ({ src, onLoad }: IUseLoading) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const imageRef = useRef<HTMLImageElement>()
  const { containerRef, isVisible } = useOnScreen()

  const handleLoad = useCallback(() => {
    if (isLoaded) {
      return
    }
    setIsLoaded(true)
    onLoad()
  }, [isLoaded, onLoad])

  useEffect(() => {
    const imageDOM = imageRef.current || document.createElement('img')
    imageDOM.addEventListener('load', handleLoad)
    if (imageDOM.src && imageDOM.complete) {
      // обязательна проверка на src, т.к. изначально оно пустое
      handleLoad()
    }
    return () => {
      imageDOM.removeEventListener('load', handleLoad)
    }
  }, [src, handleLoad])

  const currentSrc = useMemo(() => (isVisible || isLoaded ? src : undefined), [isLoaded, isVisible, src])

  return useMemo(
    () => ({
      currentSrc,
      isLoaded,
      imageRef,
      containerRef,
      isVisible,
      setIsLoaded,
    }),
    [currentSrc, isLoaded, imageRef, containerRef, isVisible, setIsLoaded],
  )
}

export default useLoading
